import { useEffect, useState } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'
import { changelogApi, type ChangelogRelease } from '../services/api'

type Status = 'loading' | 'ready' | 'error'

const CHANGELOG_TYPES = new Set(['feature', 'improvement', 'fix', 'breaking'])

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value))
}

function getTypeClass(type: string) {
  return CHANGELOG_TYPES.has(type) ? type : 'other'
}

export default function Changelog() {
  const [status, setStatus] = useState<Status>('loading')
  const [releases, setReleases] = useState<ChangelogRelease[]>([])

  useEffect(() => {
    document.body.classList.add('v2-changelog-page')
    return () => document.body.classList.remove('v2-changelog-page')
  }, [])

  useEffect(() => {
    let active = true

    changelogApi.list()
      .then((data) => {
        if (!active) return
        setReleases(data.releases || [])
        setStatus('ready')
      })
      .catch(() => {
        if (!active) return
        setStatus('error')
      })

    return () => {
      active = false
    }
  }, [])

  return (
    <>
      <SEO
        title="Change logs - OpenDeploy"
        description="Weekly OpenDeploy release notes, product updates, fixes, and shipping notes."
        path="/changelog"
      />

      <div className="od-home od-changelog-shell">
        <span className="od-corner od-corner-tl" aria-hidden="true" />
        <span className="od-corner od-corner-tr" aria-hidden="true" />
        <span className="od-corner od-corner-bl" aria-hidden="true" />
        <span className="od-corner od-corner-br" aria-hidden="true" />

        <SiteNav />

        <main className="od-changelog-main">
          <section className="od-changelog-hero" aria-labelledby="changelog-title">
            <p className="od-section-label od-section-label-plus">change logs</p>
            <h1 id="changelog-title">Built in public. Shipping fast.</h1>
            <p>Weekly notes on what changed, what shipped, and what got sharper.</p>
          </section>

          {status === 'loading' && (
            <p className="od-changelog-status">Loading change logs...</p>
          )}

          {status === 'error' && (
            <p className="od-changelog-status">Could not load change logs. Please try again soon.</p>
          )}

          {status === 'ready' && releases.length === 0 && (
            <p className="od-changelog-status">No change logs yet.</p>
          )}

          {status === 'ready' && releases.length > 0 && (
            <div className="od-changelog-list">
              {releases.map((release) => (
                <article key={release.version} className="od-release">
                  <header className="od-release-head">
                    <div>
                      <h2>{release.version}</h2>
                      <time dateTime={release.released_at}>{formatDate(release.released_at)}</time>
                    </div>
                    {release.badge && <span className="od-release-badge">{release.badge}</span>}
                  </header>

                  {release.media_url && (
                    <div className="od-release-media">
                      {release.media_type === 'video' ? (
                        <video src={release.media_url} controls />
                      ) : (
                        <img src={release.media_url} alt={`${release.version} release preview`} />
                      )}
                    </div>
                  )}

                  <div className="od-change-list">
                    {release.changes.map((change) => (
                      <section key={change.id} className="od-change">
                        <span className={`od-change-type od-change-type-${getTypeClass(change.type)}`}>
                          {change.type}
                        </span>
                        <div>
                          <h3>{change.title}</h3>
                          <p>{change.description}</p>
                        </div>
                      </section>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  )
}
